import { useState, useEffect, useCallback } from 'react';
import { Formik } from 'formik';
import * as yup from 'yup';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DataGrid } from '@mui/x-data-grid';
import {
    useTheme,
    useMediaQuery,
    Box,
    Typography,
    Button,
    IconButton,
    TextField,
    MenuItem,
    Chip,
    Collapse,
    Divider,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Autocomplete,
    Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, Close, People } from '@mui/icons-material';
import api from 'state/axios';
import { capitalizeFirstLetter } from 'state/helpers';
import CustomDatePicker from 'components/CustomDatePicker';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import DeleteConfirm from 'components/DeleteConfirm';
import SessionAttendanceDialog from './SessionAttendanceDialog';


/* ── constants ──────────────────────────────────────── */

const STATUS_OPTIONS = ['planned', 'active', 'completed', 'cancelled'];

const parseTimeString = (s) => {
    if (!s) return null;
    const [h, m] = s.split(':').map(Number);
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d;
};

const formatTimeValue = (v) => {
    if (!v) return '';
    if (typeof v === 'string') return v;
    if (isNaN(v.getTime())) return '';
    return `${v.getHours().toString().padStart(2, '0')}:${v.getMinutes().toString().padStart(2, '0')}`;
};

const STATUS_COLOR = {
    planned: 'default',
    active: 'primary',
    completed: 'success',
    cancelled: 'error',
};

const EMPTY_FORM = {
    subjects: [],
    startDate: null,
    endDate: null,
    startTime: null,
    endTime: null,
    location: '',
    maxCapacity: 20,
    status: 'planned',
    notes: '',
};

const schema = yup.object({
    startDate: yup.date().required('Required').nullable(),
    endDate: yup.date().nullable(),
    maxCapacity: yup.number().min(1).required(),
    status: yup.string().required(),
});


/* ── SessionForm ────────────────────────────────────── */

const SessionForm = ({ initial, courseDefaults, isFirstSession, allSubjects, onSave, onCancel }) => {
    const iv = initial
        ? {
              subjects: initial.subjects || [],
              startDate: initial.startDate ? new Date(initial.startDate) : null,
              endDate: initial.endDate ? new Date(initial.endDate) : null,
              startTime: initial.startTime ? parseTimeString(initial.startTime) : null,
              endTime: initial.endTime ? parseTimeString(initial.endTime) : null,
              location: initial.location || '',
              maxCapacity: initial.maxCapacity ?? 20,
              status: initial.status || 'planned',
              notes: initial.notes || '',
          }
        : (() => {
              const dateTimeDefaults = {
                  startDate: courseDefaults?.startDate ? new Date(courseDefaults.startDate) : null,
                  endDate:   courseDefaults?.endDate   ? new Date(courseDefaults.endDate)   : null,
                  startTime: courseDefaults?.startTime ? parseTimeString(courseDefaults.startTime) : null,
                  endTime:   courseDefaults?.endTime   ? parseTimeString(courseDefaults.endTime)   : null,
                  maxCapacity: courseDefaults?.maxCapacity ?? 20,
              };
              if (!isFirstSession) return { ...EMPTY_FORM, ...dateTimeDefaults };
              const courseSubjects = courseDefaults?.subjects || [];
              const resolvedSubjects = courseSubjects.length === 1
                  ? allSubjects.filter((s) => s._id === (courseSubjects[0]?._id || courseSubjects[0]))
                  : [];
              return {
                  ...EMPTY_FORM,
                  ...dateTimeDefaults,
                  subjects: resolvedSubjects,
                  location: courseDefaults?.location || '',
                  notes: courseDefaults?.notes || '',
              };
          })();

    return (
        <Formik
            initialValues={iv}
            validationSchema={schema}
            onSubmit={onSave}
            enableReinitialize
        >
            {({ values, handleChange, handleBlur, errors, touched, handleSubmit, setFieldValue }) => (
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    p={1.5}
                    gap={1.5}
                    sx={{ bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}
                >
                    {/* Subjects — multi-select */}
                    <Autocomplete
                        multiple
                        size="small"
                        options={allSubjects}
                        getOptionLabel={(o) =>
                            typeof o === 'string' ? o : `${o.subjectCode} - ${o.subjectName}`
                        }
                        value={values.subjects}
                        onChange={(_, v) => setFieldValue('subjects', v)}
                        isOptionEqualToValue={(o, v) => (o._id || o) === (v._id || v)}
                        renderValue={(selected, getItemProps) =>
                            selected.map((opt, idx) => (
                                <Chip
                                    {...getItemProps(idx)}
                                    key={opt._id || idx}
                                    label={opt.subjectCode || opt}
                                    size="small"
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Subjects"
                                placeholder={values.subjects.length === 0 ? 'Select subjects…' : ''}
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                        )}
                        sx={{ gridColumn: 'span 12' }}
                    />

                    <CustomDatePicker
                        label="Start Date"
                        name="startDate"
                        value={values.startDate}
                        onChange={setFieldValue}
                        touched={touched} errors={errors}
                        size="small"
                        variant="outlined"
                        sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}
                    />
                    <CustomDatePicker
                        label="End Date"
                        name="endDate"
                        value={values.endDate}
                        onChange={setFieldValue}
                        size="small"
                        variant="outlined"
                        sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}
                    />

                    <TimePicker
                        label="Start Time"
                        value={values.startTime}
                        onChange={(val) => setFieldValue('startTime', val)}
                        ampm={false}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}
                    />
                    <TimePicker
                        label="End Time"
                        value={values.endTime}
                        onChange={(val) => setFieldValue('endTime', val)}
                        ampm={false}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}
                    />

                    <TextField
                        fullWidth size="small" label="Location" name="location"
                        value={values.location}
                        onChange={handleChange} onBlur={handleBlur}
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={{ gridColumn: { xs: 'span 6', sm: 'span 8' } }}
                    />
                    <TextField
                        fullWidth size="small" label="Max Capacity" name="maxCapacity"
                        type="number" value={values.maxCapacity}
                        onChange={handleChange} onBlur={handleBlur}
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={{ gridColumn: { xs: 'span 6', sm: 'span 2' } }}
                    />
                    <TextField
                        fullWidth select size="small" label="Status" name="status"
                        value={values.status}
                        onChange={handleChange} onBlur={handleBlur}
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={{ gridColumn: { xs: 'span 6', sm: 'span 2' } }}
                    >
                        {STATUS_OPTIONS.map((s) => (
                            <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        fullWidth size="small" label="Notes" name="notes"
                        multiline rows={1}
                        value={values.notes}
                        onChange={handleChange} onBlur={handleBlur}
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={{ gridColumn: 'span 12' }}
                    />

                    <Box sx={{ gridColumn: 'span 12', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button size="small" variant="outlined" onClick={onCancel}>Cancel</Button>
                        <Button size="small" variant="contained" onClick={handleSubmit}>
                            {initial ? 'Update' : 'Create'}
                        </Button>
                    </Box>
                </Box>
            )}
        </Formik>
    );
};


/* ── Mobile card ────────────────────────────────────── */

const SessionCard = ({ session: s, onEdit, onManage, onDelete }) => (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
        <FlexBetween mb={0.5}>
            <Typography variant="body2" fontWeight="bold">
                {s.startDate ? format(new Date(s.startDate), 'dd/MM/yyyy') : '-'}
                {s.endDate ? ` → ${format(new Date(s.endDate), 'dd/MM/yyyy')}` : ''}
            </Typography>
            <Chip label={s.status} size="small" color={STATUS_COLOR[s.status] || 'default'} />
        </FlexBetween>

        <Typography variant="caption" color="text.secondary" display="block">
            {(s.subjects || []).map((sub) => sub.subjectCode).join(', ') || '—'}
            {s.location ? ` · ${s.location}` : ''}
        </Typography>

        <FlexBetween mt={1}>
            <Typography variant="caption" color="text.secondary">
                {s.enrollments?.filter((e) => e.status !== 'withdrawn').length ?? 0}
                {' / '}
                {s.maxCapacity} enrolled
            </Typography>
            <FlexCenter gap={0.5}>
                <Tooltip title="Edit session">
                    <IconButton size="small" onClick={() => onEdit(s)}>
                        <Edit fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Attendance & Results">
                    <IconButton size="small" color="primary" onClick={() => onManage(s)}>
                        <People fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => onDelete(s._id)}>
                        <Delete fontSize="small" />
                    </IconButton>
                </Tooltip>
            </FlexCenter>
        </FlexBetween>
    </Box>
);


/* ── TrainingSessionsDialog ─────────────────────────── */

const TrainingSessionsDialog = ({ open, onClose, courseId, courseName, course }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [sessions, setSessions] = useState([]);
    const [allSubjects, setAllSubjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [manageSession, setManageSession] = useState(null);

    const refetch = useCallback(async () => {
        const res = await api.get(`/trainingsessions?trainingCourseId=${courseId}&limit=500`);
        setSessions(res.data?.data || res.data || []);
    }, [courseId]);

    useEffect(() => {
        if (!open || !courseId) return;
        setLoading(true);
        Promise.all([
            api.get(`/trainingsessions?trainingCourseId=${courseId}&limit=500`),
            api.get('/trainingsubjects/active'),
        ])
            .then(([sessRes, subRes]) => {
                setSessions(sessRes.data?.data || sessRes.data || []);
                setAllSubjects(subRes.data || []);
            })
            .catch(() => toast.error('Failed to load sessions!'))
            .finally(() => setLoading(false));
    }, [open, courseId]);

    const handleOpenForm = (session = null) => {
        setEditTarget(session);
        setFormOpen(true);
    };

    const handleCloseForm = () => {
        setFormOpen(false);
        setEditTarget(null);
    };

    const handleSave = async (values) => {
        try {
            const payload = {
                ...values,
                trainingCourseId: courseId,
                subjects: (values.subjects || []).map((s) => s._id || s),
                startTime: formatTimeValue(values.startTime),
                endTime: formatTimeValue(values.endTime),
            };
            if (editTarget) {
                await api.put(`/trainingsessions/update/${editTarget._id}`, payload);
                toast.success('Session updated.');
            } else {
                await api.post('/trainingsessions/new', payload);
                toast.success('Session created.');
            }
            await refetch();
            handleCloseForm();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save training session!');
        }
    };

    const handleDeleteRequest = (id) => {
        setDeleteId(id);
        setDeleteOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await api.patch('/trainingsessions/soft-delete', { ids: [deleteId] });
            setSessions((prev) => prev.filter((s) => s._id !== deleteId));
            toast.success('Session deleted.');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Delete failed.');
        } finally {
            setDeleteOpen(false);
            setDeleteId(null);
        }
    };

    const columns = [
        {
            field: 'no', 
            headerName: 'No', 
            width: 55, 
            sortable: false, 
            filterable: false,
            renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
        },
        {
            field: 'subjects', 
            headerName: 'Subject', 
            flex: 2, 
            minWidth: 160, 
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', height: '100%' }}>
                    {(params.value || []).length
                        ? (params.value || []).map((s) => (
                              <Chip key={s._id} label={s.subjectCode} size="small" variant="outlined" />
                          ))
                        : <Typography variant="caption" color="text.secondary">—</Typography>
                    }
                </Box>
            ),
        },
        {
            field: 'startDate', 
            headerName: 'StartDate', 
            width: 95,
            valueFormatter: (value) => value ? format(new Date(value), 'dd/MM/yy') : '-',
        },
        {
            field: 'endDate', 
            headerName: 'EndDate', 
            width: 95,
            valueFormatter: (value) => value ? format(new Date(value), 'dd/MM/yy') : '-',
        },
        {
            field: 'startTime', 
            headerName: 'StartTime', 
            width: 70,
            valueFormatter: (value) => value || '-',
        },
        {
            field: 'endTime', 
            headerName: 'EndTime', 
            width: 70,
            valueFormatter: (value) => value || '-',
        },
        {
            field: 'location', 
            headerName: 'Location', 
            flex: 1, 
            minWidth: 100,
        },
        {
            field: 'enrollments', 
            headerName: 'Capacity', 
            width: 80, 
            sortable: false,
            renderCell: (params) => {
                const active = (params.value || []).filter((e) => e.status !== 'withdrawn').length;
                const total = params.row.maxCapacity;
                return (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        height="100%"
                    >
                        <Typography variant="body2">
                            {active} / {total}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'status', 
            headerName: 'Status', 
            width: 110,
            renderCell: (params) => (
                <Chip
                    label={capitalizeFirstLetter(params.value)}
                    size="small"
                    color={STATUS_COLOR[params.value] || 'default'}
                    sx={{ flexShrink: 0, minWidth: 76, justifyContent: 'center' }}
                />
            ),
        },
        {
            field: 'action', 
            headerName: 'Action', 
            width: 140, 
            sortable: false, 
            filterable: false,
            renderCell: (params) => (
                <FlexCenter 
                    gap={0.5} 
                    sx={{ width: '100%', height: '100%' }}
                >
                    <Tooltip title="Edit session">
                        <IconButton 
                            onClick={() => handleOpenForm(params.row)}
                        >
                            <Edit />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Attendance & Results">
                        <IconButton 
                            color="primary" 
                            onClick={() => setManageSession(params.row)}
                        >
                            <People />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton 
                            color="error" 
                            onClick={() => handleDeleteRequest(params.row._id)}
                        >
                            <Delete />
                        </IconButton>
                    </Tooltip>
                </FlexCenter>
            ),
        },
    ];

    const deleteTarget = sessions.find((s) => s._id === deleteId);

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                slotProps={{ paper: { sx: { height: '100%', display: 'flex', flexDirection: 'column' } } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <FlexBetween>
                        <Box>
                            <Typography variant="h6" fontWeight="bold">
                                Training Sessions
                            </Typography>
                            {courseName && (
                                <Typography variant="caption" color="text.secondary">
                                    {courseName}
                                </Typography>
                            )}
                        </Box>
                        <FlexCenter gap={1}>
                            <Button
                                size="small"
                                variant={formOpen && !editTarget ? 'contained' : 'outlined'}
                                startIcon={<Add />}
                                onClick={() => formOpen && !editTarget ? handleCloseForm() : handleOpenForm()}
                            >
                                Add Session
                            </Button>
                            {isMobile && (
                                <IconButton size="small" onClick={onClose}>
                                    <Close />
                                </IconButton>
                            )}
                        </FlexCenter>
                    </FlexBetween>
                </DialogTitle>

                <DialogContent
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflowX: 'hidden',
                        px: { xs: 1.5, sm: 2.5 },
                        pb: 1,
                    }}
                >
                    <Collapse in={formOpen}>
                        <SessionForm
                            initial={editTarget}
                            courseDefaults={editTarget ? null : course}
                            isFirstSession={!editTarget && sessions.length === 0}
                            allSubjects={allSubjects}
                            onSave={handleSave}
                            onCancel={handleCloseForm}
                        />
                        <Divider sx={{ mb: 1 }} />
                    </Collapse>

                    {loading ? (
                        <FlexCenter height={200}><CircularProgress /></FlexCenter>
                    ) : isMobile ? (
                        <Box sx={{ flex: 1, overflowY: 'auto' }}>
                            {sessions.length === 0 ? (
                                <FlexCenter height={200}>
                                    <Typography variant="body2" color="text.secondary">
                                        No sessions yet. Click "Add Session" to create one.
                                    </Typography>
                                </FlexCenter>
                            ) : (
                                <Stack gap={1.5}>
                                    {sessions.map((s) => (
                                        <SessionCard
                                            key={s._id}
                                            session={s}
                                            onEdit={handleOpenForm}
                                            onManage={setManageSession}
                                            onDelete={handleDeleteRequest}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <DataGrid
                                getRowId={(row) => row._id}
                                rows={sessions}
                                columns={columns}
                                loading={loading}
                                pageSizeOptions={[25, 50, 100]}
                                initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
                                disableRowSelectionOnClick
                                sx={{
                                    height: '100%',
                                    '& .MuiDataGrid-cell': { alignItems: 'center' },
                                    '& .MuiDataGrid-columnHeader': { fontWeight: 'bold' },
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>

                {!isMobile && (
                    <DialogActions>
                        <Button variant="outlined" color="secondary" onClick={() => { setSessions([]); onClose(); }}>
                            Close
                        </Button>
                    </DialogActions>
                )}
            </Dialog>

            <DeleteConfirm
                open={deleteOpen}
                onClose={() => { setDeleteOpen(false); setDeleteId(null); }}
                onConfirm={handleDeleteConfirm}
                message={`Delete session on ${deleteTarget?.startDate ? format(new Date(deleteTarget.startDate), 'dd/MM/yyyy') : '?'}?`}
            />

            <SessionAttendanceDialog
                open={!!manageSession}
                session={manageSession}
                onClose={() => setManageSession(null)}
                onSaved={refetch}
            />
        </LocalizationProvider>
    );
};

export default TrainingSessionsDialog;
